/**
 * Studio Production Service
 * Production jobs: draft → in_progress → completed / cancelled
 * Inventory impact ONLY when status = completed (finished goods ADD via stock_movements)
 * Audit: studio_production_logs for create/update/status_change
 */

import { supabase } from '@/lib/supabase';
import { productService } from '@/app/services/productService';
import { settingsService } from '@/app/services/settingsService';
import { accountHelperService } from '@/app/services/accountHelperService';
import { accountingService, type JournalEntry, type JournalEntryLine } from '@/app/services/accountingService';
import { resolveWorkerPayablePostingAccountId } from '@/app/services/partySubledgerAccountService';
import { parseStudioWorkflowDate } from '@/app/lib/studioOrderDisplay';
import { localNowDateString, toLocalDateString } from '@/app/utils/localDate';

const STUDIO_BILL_LOCKED_MSG =
  'Bill already generated — production pipeline is locked. Use invoice update on web or mobile invoice screen only.';

async function productionIdForStage(stageId: string): Promise<string | null> {
  const { data } = await supabase
    .from('studio_production_stages')
    .select('production_id')
    .eq('id', stageId)
    .maybeSingle();
  return (data as { production_id?: string } | null)?.production_id ?? null;
}

async function assertProductionPipelineUnlockedForStage(stageId: string): Promise<void> {
  const productionId = await productionIdForStage(stageId);
  if (!productionId) return;
  const { data: prod } = await supabase
    .from('studio_productions')
    .select('generated_invoice_item_id')
    .eq('id', productionId)
    .maybeSingle();
  if ((prod as { generated_invoice_item_id?: string | null } | null)?.generated_invoice_item_id) {
    throw new Error(STUDIO_BILL_LOCKED_MSG);
  }
}

/**
 * FK studio_production_stages.assigned_worker_id → workers(id).
 * Upsert from contacts (type=worker) when sync trigger missed.
 */
export async function ensureWorkerRowForAssign(companyId: string, workerId: string): Promise<string> {
  const id = workerId?.trim();
  if (!id) throw new Error('Worker is required to assign this step.');
  const { data: existing } = await supabase
    .from('workers')
    .select('id')
    .eq('id', id)
    .eq('company_id', companyId)
    .maybeSingle();
  if (existing) return id;

  const { data: contact } = await supabase
    .from('contacts')
    .select('id, company_id, name, phone, mobile, worker_role, is_active, type')
    .eq('id', id)
    .eq('company_id', companyId)
    .maybeSingle();
  if (!contact) {
    throw new Error('Worker not found. Open Contacts → Workers and save the worker again.');
  }
  const ct = (contact as { type?: string }).type;
  const isWorker = ct === 'worker' || String(ct) === 'worker';
  if (!isWorker) {
    throw new Error('Selected contact is not a worker. Use Contacts → Workers.');
  }
  const c = contact as {
    id: string;
    company_id: string;
    name?: string | null;
    phone?: string | null;
    mobile?: string | null;
    worker_role?: string | null;
    is_active?: boolean | null;
  };
  const { error } = await supabase.from('workers').upsert(
    {
      id: c.id,
      company_id: c.company_id,
      name: (c.name || 'Worker').slice(0, 255),
      phone: c.phone || c.mobile || null,
      worker_type: c.worker_role || 'General',
      is_active: c.is_active !== false,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'id' }
  );
  if (error) throw new Error(error.message || 'Could not sync worker to workers table.');
  return id;
}

async function companyIdForStage(stageId: string): Promise<string | null> {
  const productionId = await productionIdForStage(stageId);
  if (!productionId) return null;
  const { data: prod } = await supabase
    .from('studio_productions')
    .select('company_id')
    .eq('id', productionId)
    .maybeSingle();
  return (prod as { company_id?: string } | null)?.company_id ?? null;
}

/** Contact first (parity with mobile), then `workers` when id is a worker row or contact name missing. */
async function resolveStageWorker(stage: any): Promise<void> {
  if (!stage?.assigned_worker_id) return;
  const wid = stage.assigned_worker_id as string;
  const { data: c } = await supabase.from('contacts').select('id, name').eq('id', wid).maybeSingle();
  const contactName = (c as { name?: string } | null)?.name?.trim() ?? '';
  if (contactName) {
    stage.worker = { id: wid, name: contactName };
    return;
  }
  const { data: w } = await supabase.from('workers').select('id, name').eq('id', wid).maybeSingle();
  if (w) {
    stage.worker = { id: wid, name: (w as { name?: string }).name || '' };
    return;
  }
  stage.worker = { id: wid, name: contactName };
}

/** Batch: same resolution as mobile `getStudioStages` (contacts + workers fallback). */
async function workerDisplayNamesByIds(ids: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const unique = [...new Set(ids)].filter(Boolean);
  if (unique.length === 0) return out;
  const { data: contacts } = await supabase.from('contacts').select('id, name').in('id', unique);
  for (const row of contacts || []) {
    const r = row as { id: string; name?: string | null };
    if (r.id) out.set(r.id, (r.name || '').trim());
  }
  const needWorker = unique.filter((id) => !String(out.get(id) ?? '').trim());
  if (needWorker.length === 0) return out;
  const { data: workers } = await supabase.from('workers').select('id, name').in('id', needWorker);
  for (const row of workers || []) {
    const r = row as { id: string; name?: string | null };
    if (!r.id) continue;
    const nm = (r.name || '').trim();
    if (!nm) continue;
    if (String(out.get(r.id) ?? '').trim()) continue;
    out.set(r.id, nm);
  }
  return out;
}

export type StudioProductionStatus = 'draft' | 'in_progress' | 'completed' | 'cancelled';

/** Z2: per-stage paid/due from payments.worker_payment + worker_ledger (FIFO), for Studio UI. */
export type StageWorkerPaymentDetail = {
  status: 'unpaid' | 'partial' | 'paid';
  paidAmount: number;
  remainingDue: number;
};

const STAGE_PAY_EPS = 0.02;

async function fetchStageWorkerPaymentDetailsImpl(
  companyId: string,
  stageIds: string[],
  branchId?: string | null
): Promise<Record<string, StageWorkerPaymentDetail>> {
  const out: Record<string, StageWorkerPaymentDetail> = {};
  if (!companyId || stageIds.length === 0) return out;

  const { data: stages, error: stErr } = await supabase
    .from('studio_production_stages')
    .select('id, production_id, assigned_worker_id, cost, expected_cost, status, completed_at')
    .in('id', stageIds);
  if (stErr || !stages?.length) {
    stageIds.forEach((id) => {
      out[id] = { status: 'unpaid', paidAmount: 0, remainingDue: 0 };
    });
    return out;
  }

  const prodIds = [...new Set((stages as any[]).map((s) => s.production_id).filter(Boolean))];
  const { data: prods } = await supabase
    .from('studio_productions')
    .select('id, company_id')
    .in('id', prodIds);
  const prodCompany = new Map((prods || []).map((p: any) => [p.id, p.company_id as string]));

  const stagesInCo = (stages as any[]).filter((s) => prodCompany.get(s.production_id) === companyId);

  let payQuery = supabase
    .from('payments')
    .select('reference_id, amount, branch_id')
    .eq('company_id', companyId)
    .eq('reference_type', 'worker_payment');
  const { data: payRows } = await payQuery;
  const paymentByWorker = new Map<string, number>();
  (payRows || []).forEach((r: any) => {
    const wid = r.reference_id as string | null;
    if (!wid) return;
    if (branchId && branchId !== 'all') {
      const bid = r.branch_id as string | null | undefined;
      if (bid != null && bid !== branchId) return;
    }
    paymentByWorker.set(wid, (paymentByWorker.get(wid) ?? 0) + (Number(r.amount) || 0));
  });

  const { data: ledgerRows } = await supabase
    .from('worker_ledger_entries')
    .select('reference_id, status')
    .eq('reference_type', 'studio_production_stage')
    .in('reference_id', stageIds);
  const ledgerPaid = new Set<string>();
  (ledgerRows || []).forEach((r: any) => {
    if (String(r.status || '').toLowerCase() === 'paid') ledgerPaid.add(r.reference_id);
  });

  const byWorker = new Map<string, any[]>();
  for (const s of stagesInCo) {
    const w = s.assigned_worker_id as string | null;
    if (!w) continue;
    if (!byWorker.has(w)) byWorker.set(w, []);
    byWorker.get(w)!.push(s);
  }

  for (const [workerId, arr] of byWorker) {
    let pool = paymentByWorker.get(workerId) ?? 0;
    const sorted = [...arr].sort((a, b) => {
      const ca = (a.completed_at as string) || '';
      const cb = (b.completed_at as string) || '';
      if (ca && cb) return ca.localeCompare(cb);
      if (ca) return -1;
      if (cb) return 1;
      return 0;
    });
    for (const s of sorted) {
      const sid = s.id as string;
      const completed = String(s.status || '').toLowerCase() === 'completed';
      const cost = completed
        ? Number(s.cost) || 0
        : Number(s.expected_cost) || Number(s.cost) || 0;

      if (ledgerPaid.has(sid)) {
        out[sid] = { status: 'paid', paidAmount: cost, remainingDue: 0 };
        pool = Math.max(0, pool - cost);
        continue;
      }
      if (!completed || cost <= 0) {
        out[sid] = { status: 'unpaid', paidAmount: 0, remainingDue: cost };
        continue;
      }
      const applied = Math.min(cost, pool);
      pool -= applied;
      const due = Math.max(0, cost - applied);
      let st: 'unpaid' | 'partial' | 'paid';
      if (due <= STAGE_PAY_EPS) st = 'paid';
      else if (applied > STAGE_PAY_EPS) st = 'partial';
      else st = 'unpaid';
      out[sid] = { status: st, paidAmount: applied, remainingDue: due };
    }
  }

  for (const s of stagesInCo) {
    const sid = s.id as string;
    if (out[sid]) continue;
    const completed = String(s.status || '').toLowerCase() === 'completed';
    const cost = completed
      ? Number(s.cost) || 0
      : Number(s.expected_cost) || Number(s.cost) || 0;
    out[sid] = { status: 'unpaid', paidAmount: 0, remainingDue: cost };
  }

  for (const id of stageIds) {
    if (!out[id]) out[id] = { status: 'unpaid', paidAmount: 0, remainingDue: 0 };
  }
  return out;
}

export interface StudioProduction {
  id: string;
  company_id: string;
  branch_id: string;
  sale_id?: string | null;
  production_no: string;
  production_date: string;
  product_id: string;
  variation_id?: string | null;
  quantity: number;
  boxes?: number | null;
  pieces?: number | null;
  unit: string;
  estimated_cost: number;
  actual_cost: number;
  status: StudioProductionStatus;
  start_date?: string | null;
  expected_date?: string | null;
  completed_at?: string | null;
  assigned_worker_id?: string | null;
  assigned_machine_or_karigar?: string | null;
  notes?: string | null;
  instructions?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  product?: { id: string; name: string; sku?: string };
  worker?: { id: string; name: string };
  /** Replica / studio outfit title (Sale Form); canonical display name when set. */
  design_name?: string | null;
  /** Product created for this studio order (invoice line). Set when studio product line is added. */
  generated_product_id?: string | null;
  /** sales_items.id of the generated studio line. Pricing sync updates only this item. */
  generated_invoice_item_id?: string | null;
  /** Pricing Calculator: percentage | fixed */
  profit_margin_mode?: string | null;
  /** Pricing Calculator: margin % or fixed amount */
  profit_margin_value?: number | null;
}

export type StudioProductionStageType = 'dyer' | 'stitching' | 'handwork' | 'extra';
export type StudioProductionStageStatus = 'pending' | 'assigned' | 'in_progress' | 'completed';

export interface StudioProductionStage {
  id: string;
  production_id: string;
  stage_type: StudioProductionStageType;
  assigned_worker_id?: string | null;
  cost: number;
  status: StudioProductionStageStatus;
  expected_cost?: number | null;
  assigned_at?: string | null;
  journal_entry_id?: string | null;
  expected_completion_date?: string | null;
  completed_at?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  worker?: { id: string; name: string };
}

export interface StudioProductionLog {
  id: string;
  production_id: string;
  action_type: string;
  old_value?: Record<string, unknown> | null;
  new_value?: Record<string, unknown> | null;
  performed_by?: string | null;
  performed_at: string;
}

export interface CreateProductionInput {
  company_id: string;
  branch_id: string;
  sale_id: string;
  production_no: string;
  production_date: string;
  product_id: string;
  variation_id?: string | null;
  quantity: number;
  boxes?: number | null;
  pieces?: number | null;
  unit?: string;
  estimated_cost?: number;
  actual_cost?: number;
  assigned_worker_id?: string | null;
  assigned_machine_or_karigar?: string | null;
  notes?: string | null;
  instructions?: string | null;
  start_date?: string | null;
  expected_date?: string | null;
  created_by?: string | null;
}

function logAction(
  productionId: string,
  actionType: string,
  oldValue: Record<string, unknown> | null,
  newValue: Record<string, unknown> | null,
  performedBy?: string | null
) {
  return supabase.from('studio_production_logs').insert({
    production_id: productionId,
    action_type: actionType,
    old_value: oldValue,
    new_value: newValue,
    performed_by: performedBy || null,
  });
}

/** PHASE 3: Create journal entry Dr Production Expense (5000) Cr Worker Payable (2010). Links to stage + sale. */
async function createProductionCostJournalEntry(params: {
  companyId: string;
  branchId: string | null;
  stageId: string;
  workerId: string;
  productionNo: string;
  saleId: string | null;
  amount: number;
  stageType: string;
  performedBy?: string | null;
}): Promise<string | null> {
  const { companyId, branchId, stageId, workerId, productionNo, saleId, amount, stageType, performedBy } = params;
  if (amount <= 0) return null;
  const costAccount = await accountHelperService.getAccountByCode('5000', companyId);
  const workerPayableAccountId = await resolveWorkerPayablePostingAccountId(companyId, workerId);
  if (!costAccount?.id || !workerPayableAccountId) {
    console.warn('[studioProductionService] Cost of Production (5000) or Worker Payable (2010) account not found');
    return null;
  }
  const entryNo = `JE-STD-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
  const entryDate = new Date().toISOString().split('T')[0];
  const entry: JournalEntry = {
    id: '',
    company_id: companyId,
    branch_id: branchId || undefined,
    entry_no: entryNo,
    entry_date: entryDate,
    description: `Studio production ${productionNo} – ${stageType} stage completed`,
    reference_type: 'studio_production_stage',
    reference_id: stageId,
    created_by: performedBy || undefined,
  };
  const lines: JournalEntryLine[] = [
    { id: '', journal_entry_id: '', account_id: costAccount.id, debit: amount, credit: 0, description: `Production cost – ${stageType}` },
    { id: '', journal_entry_id: '', account_id: workerPayableAccountId, debit: 0, credit: amount, description: `Worker payable – ${stageType}` },
  ];
  const result = await accountingService.createEntry(entry, lines);
  const billJeId = (result as any)?.id ?? null;
  if (billJeId && workerId) {
    const { applyWorkerAdvanceAgainstNewBill } = await import('@/app/services/workerAdvanceService');
    await applyWorkerAdvanceAgainstNewBill({
      companyId,
      branchId,
      workerId,
      stageId,
      billAmount: amount,
      billJournalEntryId: billJeId,
      productionNo,
      stageType,
      performedBy: performedBy ?? null,
    });
  }
  return billJeId;
}

/** PHASE 4: Create reversal entry Dr Worker Payable Cr Production Expense (same amount). */
async function createProductionCostReversalEntry(params: {
  companyId: string;
  branchId: string | null;
  stageId: string;
  workerId?: string | null;
  productionNo: string;
  amount: number;
  stageType: string;
  performedBy?: string | null;
  /** Defaults to stage-reopen wording; use for sale-cancel reversals. */
  description?: string | null;
}): Promise<string | null> {
  const { companyId, branchId, stageId, workerId, productionNo, amount, stageType, performedBy, description } = params;
  if (amount <= 0) return null;
  const costAccount = await accountHelperService.getAccountByCode('5000', companyId);
  const workerPayableAccountId = await resolveWorkerPayablePostingAccountId(companyId, workerId ?? null);
  if (!costAccount?.id || !workerPayableAccountId) return null;
  const entryNo = `JE-STD-REV-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
  const entryDate = new Date().toISOString().split('T')[0];
  const entry: JournalEntry = {
    id: '',
    company_id: companyId,
    branch_id: branchId || undefined,
    entry_no: entryNo,
    entry_date: entryDate,
    description:
      description?.trim() ||
      `Reversal: Studio ${productionNo} – ${stageType} stage reopened`,
    reference_type: 'studio_production_stage_reversal',
    reference_id: stageId,
    created_by: performedBy || undefined,
  };
  const lines: JournalEntryLine[] = [
    { id: '', journal_entry_id: '', account_id: workerPayableAccountId, debit: amount, credit: 0, description: `Reversal worker payable – ${stageType}` },
    { id: '', journal_entry_id: '', account_id: costAccount.id, debit: 0, credit: amount, description: `Reversal production cost – ${stageType}` },
  ];
  const result = await accountingService.createEntry(entry, lines);
  return (result as any)?.id ?? null;
}

/**
 * Post-bill backflush: for each completed stage with cost and worker, create Dr 5000 / Cr payable JE + worker_ledger
 * only when `journal_entry_id` is NULL. Stages that already have a posted JE (legacy mid-flight) are skipped to avoid double-posting.
 */
async function postPendingStageCostsAndLedgersForProduction(
  productionId: string,
  ensureWorkerLedger: (stageId: string, cost: number, workerId: string) => Promise<void>,
  performedBy?: string | null
): Promise<void> {
  const { data: prodRow, error: prodErr } = await supabase
    .from('studio_productions')
    .select('id, company_id, branch_id, production_no, sale_id')
    .eq('id', productionId)
    .single();
  if (prodErr || !prodRow) throw new Error('Production not found');
  const production = prodRow as any;
  const { data: stageRows, error: stErr } = await supabase
    .from('studio_production_stages')
    .select('id, status, cost, assigned_worker_id, stage_type, journal_entry_id')
    .eq('production_id', productionId);
  if (stErr) throw stErr;
  for (const s of (stageRows || []) as any[]) {
    if (String(s.status || '').toLowerCase() !== 'completed') continue;
    const cost = Number(s.cost) || 0;
    const workerId = s.assigned_worker_id as string | null;
    if (cost <= 0 || !workerId) continue;
    if (s.journal_entry_id) continue;
    const stageId = s.id as string;
    const journalId = await createProductionCostJournalEntry({
      companyId: production.company_id,
      branchId: production.branch_id || null,
      stageId,
      workerId,
      productionNo: production.production_no,
      saleId: production.sale_id ?? null,
      amount: cost,
      stageType: s.stage_type || 'stage',
      performedBy: performedBy ?? null,
    });
    if (!journalId) {
      console.warn('[studioProductionService] postPending: could not create JE for stage', stageId);
      continue;
    }
    const { error: upErr } = await supabase
      .from('studio_production_stages')
      .update({ journal_entry_id: journalId })
      .eq('id', stageId);
    if (upErr) throw new Error(`Failed to link journal to stage: ${upErr.message}`);
    await ensureWorkerLedger(stageId, cost, workerId);
  }
}

/** Backfill: create studio_production + stages for studio sales that don't have one (so they show on Studio dashboard). Exported for call from StudioDashboardNew. */
export async function ensureStudioProductionsForCompany(companyId: string): Promise<void> {
  try {
    const { data: sales, error: salesErr } = await supabase
      .from('sales')
      .select('id, company_id, branch_id, invoice_no, invoice_date, created_by')
      .eq('company_id', companyId)
      .eq('is_studio', true)
      .neq('status', 'cancelled');
    if (salesErr || !sales?.length) return;

    const { data: prods } = await supabase
      .from('studio_productions')
      .select('sale_id')
      .eq('company_id', companyId);
    const hasProduction = new Set((prods || []).map((p: { sale_id: string }) => p.sale_id));
    const missing = (sales as { id: string; company_id: string; branch_id: string; invoice_no: string; invoice_date: string; created_by: string | null }[]).filter((s) => !hasProduction.has(s.id));
    if (missing.length === 0) return;

    let items: { sale_id: string; product_id: string; variation_id: string | null; quantity: number }[] = [];
    const { data: itemsSales } = await supabase
      .from('sales_items')
      .select('sale_id, product_id, variation_id, quantity')
      .in('sale_id', missing.map((s) => s.id));
    if (itemsSales?.length) {
      items = (itemsSales as { sale_id: string; product_id: string; variation_id: string | null; quantity: number }[]).map((r) => ({
        sale_id: r.sale_id,
        product_id: r.product_id,
        variation_id: r.variation_id ?? null,
        quantity: Math.max(0.01, Number(r.quantity) || 1),
      }));
    } else {
      const { data: itemsLegacy } = await supabase
        .from('sale_items')
        .select('sale_id, product_id, variation_id, quantity')
        .in('sale_id', missing.map((s) => s.id));
      if (itemsLegacy?.length) {
        items = (itemsLegacy as { sale_id: string; product_id: string; variation_id: string | null; quantity: number }[]).map((r) => ({
          sale_id: r.sale_id,
          product_id: r.product_id,
          variation_id: r.variation_id ?? null,
          quantity: Math.max(0.01, Number(r.quantity) || 1),
        }));
      }
    }
    const firstItemBySale = new Map<string, { product_id: string; variation_id: string | null; quantity: number }>();
    for (const it of items) {
      if (!firstItemBySale.has(it.sale_id)) firstItemBySale.set(it.sale_id, { product_id: it.product_id, variation_id: it.variation_id, quantity: it.quantity });
    }

    for (const sale of missing) {
      const first = firstItemBySale.get(sale.id);
      if (!first?.product_id) continue;
      const { documentNumberService } = await import('@/app/services/documentNumberService');
      const productionNo = await documentNumberService.getNextProductionNumber(sale.company_id).catch(() => `PRD-${sale.invoice_no || sale.id}`);
      const productionDate = sale.invoice_date ? toLocalDateString(sale.invoice_date) : localNowDateString();
      const { data: inserted, error: insErr } = await supabase
        .from('studio_productions')
        .insert({
          company_id: sale.company_id,
          branch_id: sale.branch_id,
          sale_id: sale.id,
          production_no: productionNo,
          production_date: productionDate,
          product_id: first.product_id,
          variation_id: first.variation_id,
          quantity: first.quantity,
          status: 'draft',
          created_by: sale.created_by ?? null,
        })
        .select('id')
        .single();
      if (insErr) {
        if (insErr.code === '23505') continue;
        console.warn('[studioProductionService] ensureStudioProductions backfill insert failed:', insErr);
        continue;
      }
      const prodId = (inserted as { id: string })?.id;
      if (prodId) {
        await supabase.from('studio_production_stages').insert([
          { production_id: prodId, stage_type: 'dyer', cost: 0, status: 'pending' },
          { production_id: prodId, stage_type: 'handwork', cost: 0, status: 'pending' },
          { production_id: prodId, stage_type: 'stitching', cost: 0, status: 'pending' },
        ]);
      }
    }
  } catch (e) {
    console.warn('[studioProductionService] ensureStudioProductionsForCompany failed:', e);
  }
}

export const studioProductionService = {
  async getProductions(companyId: string, branchId?: string | null): Promise<StudioProduction[]> {
    try {
      await ensureStudioProductionsForCompany(companyId);
      let query = supabase
        .from('studio_productions')
        .select(`
          *,
          product:products!product_id(id, name, sku),
          worker:workers(id, name),
          sale:sales(invoice_no)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (branchId && branchId !== 'all') {
        query = query.eq('branch_id', branchId);
      }

      const { data, error } = await query;
      if (error) {
        if (error.code === 'PGRST116' || error.message?.includes('does not exist')) return [];
        throw error;
      }
      return (data || []) as StudioProduction[];
    } catch (e: any) {
      if (e?.code === 'PGRST204' || e?.message?.includes('relation') || e?.message?.includes('does not exist'))
        return [];
      throw e;
    }
  },

  /** Paginated: last N productions (for pipeline). Default limit 100. */
  async getProductionsPage(
    companyId: string,
    opts: { limit?: number; offset?: number; branchId?: string | null }
  ): Promise<StudioProduction[]> {
    const limit = Math.min(opts.limit ?? 100, 500);
    const offset = opts.offset ?? 0;
    try {
      let query = supabase
        .from('studio_productions')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      if (opts.branchId && opts.branchId !== 'all') {
        query = query.eq('branch_id', opts.branchId);
      }
      const { data, error } = await query;
      if (error) {
        if (error.code === 'PGRST116' || error.message?.includes('does not exist')) return [];
        throw error;
      }
      return (data || []) as StudioProduction[];
    } catch (e: any) {
      if (e?.code === 'PGRST204' || e?.message?.includes('relation') || e?.message?.includes('does not exist'))
        return [];
      throw e;
    }
  },

  async getProductionById(id: string): Promise<StudioProduction | null> {
    const { data, error } = await supabase
      .from('studio_productions')
      .select(`
        *,
        product:products!product_id(id, name, sku),
        worker:workers(id, name),
        sale:sales(invoice_no)
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data as StudioProduction;
  },

  async getProductionsBySaleId(saleId: string): Promise<StudioProduction[]> {
    try {
      const { data, error } = await supabase
        .from('studio_productions')
        .select(`*, product:products!product_id(id, name, sku), worker:workers(id, name)`)
        .eq('sale_id', saleId)
        .order('created_at', { ascending: false });
      if (error) {
        if (error.code === 'PGRST116' || error.message?.includes('sale_id')) return [];
        throw error;
      }
      return (data || []) as StudioProduction[];
    } catch (e: any) {
      if (e?.code === '42703' || e?.message?.includes('sale_id')) return [];
      throw e;
    }
  },

  /** Batch: get productions for multiple sale IDs in one query (for Studio Sales list page). */
  async getProductionsBySaleIds(saleIds: string[]): Promise<StudioProduction[]> {
    if (saleIds.length === 0) return [];
    try {
      const { data, error } = await supabase
        .from('studio_productions')
        .select('id, sale_id, production_no, company_id, branch_id, status, created_at, design_name, generated_invoice_item_id')
        .in('sale_id', saleIds)
        .order('created_at', { ascending: false });
      if (error) {
        if (error.code === 'PGRST116' || error.message?.includes('sale_id')) return [];
        throw error;
      }
      return (data || []) as StudioProduction[];
    } catch (e: any) {
      if (e?.code === '42703' || e?.message?.includes('sale_id')) return [];
      throw e;
    }
  },

  /** Set the generated product and invoice item for this production (when studio product line is added to sale). If production is already completed, post stock to generated product so Stock Ledger shows history. */
  async setGeneratedInvoiceItem(
    productionId: string,
    generatedProductId: string,
    generatedInvoiceItemId: string
  ): Promise<void> {
    const existing = await this.getProductionById(productionId);
    const { error } = await supabase
      .from('studio_productions')
      .update({
        generated_product_id: generatedProductId,
        generated_invoice_item_id: generatedInvoiceItemId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', productionId);
    if (error) throw error;
    if (!existing || (existing as any).status !== 'completed') return;
    const saleId = (existing as { sale_id?: string | null }).sale_id;
    if (saleId) {
      const { ensureStudioProductionInForSale } = await import('./studioStockLifecycleService');
      try {
        await ensureStudioProductionInForSale(saleId);
      } catch (e) {
        console.warn('[studio_production] ensureStudioProductionInForSale (linked invoice):', e);
      }
    }
  },

  /** Update profit margin for Pricing Calculator (persists so values show after back/forward). */
  async updateProductionProfitMargin(
    productionId: string,
    payload: { mode: 'percentage' | 'fixed'; value: number }
  ): Promise<void> {
    const { error } = await supabase
      .from('studio_productions')
      .update({
        profit_margin_mode: payload.mode,
        profit_margin_value: payload.value,
        updated_at: new Date().toISOString(),
      })
      .eq('id', productionId);
    if (error) throw error;
  },

  /**
   * Create generated studio product + add studio line to sale + save links.
   * Call this at NEW studio sale creation so sync has deterministic links (no fallback inference).
   * Fabric line = first item (production.product_id). Studio line = this added line (price 0 until sync).
   */
  async createGeneratedProductAndStudioLine(params: {
    productionId: string;
    saleId: string;
    invoiceNo: string;
    fabricProductId: string;
    companyId: string;
    createdBy: string | null;
  }): Promise<{ generatedProductId: string; generatedInvoiceItemId: string }> {
    const { productionId, saleId, invoiceNo, fabricProductId, companyId, createdBy } = params;
    const { documentNumberService } = await import('@/app/services/documentNumberService');
    const sku =
      await documentNumberService.getNextProductionProductSKU(companyId).catch(() =>
        `STD-PROD-${invoiceNo}-${Date.now().toString(36)}`);
    const productName = `Studio – ${invoiceNo}`;
    if (import.meta.env?.DEV) {
      console.log('[studio_production] createGeneratedProductAndStudioLine: creating product', { productName, sku });
    }
    const product = await productService.createProduct({
      company_id: companyId,
      name: productName,
      sku,
      category_id: null as any,
      cost_price: 0,
      retail_price: 0,
      wholesale_price: 0,
      current_stock: 0,
      min_stock: 0,
      max_stock: 1000,
      has_variations: false,
      is_rentable: false,
      is_sellable: true,
      track_stock: true,
      is_active: true,
      product_type: 'production',
    }) as { id: string; name: string; sku: string };
    if (import.meta.env?.DEV) {
      console.log('[studio_production] product created', product.id);
    }
    const itemRow = {
      sale_id: saleId,
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      quantity: 1,
      unit: 'piece',
      unit_price: 0,
      discount_amount: 0,
      tax_amount: 0,
      total: 0,
      is_studio_product: true,
    };
    const { data: insertedItem, error: itemErr } = await supabase
      .from('sales_items')
      .insert(itemRow)
      .select('id')
      .single();
    if (itemErr) {
      // P1-2: fallback also writes to sales_items (canonical) — was: sale_items (legacy)
      // Strip is_studio_product (column may not exist in sales_items schema)
      const fallbackPayload: Record<string, unknown> = {
        sale_id: saleId,
        product_id: product.id,
        product_name: product.name,
        sku: product.sku,
        quantity: 1,
        unit_price: 0,
        total: 0,
      };
      const { data: fallbackData, error: fallbackErr } = await supabase
        .from('sales_items')
        .insert(fallbackPayload)
        .select('id')
        .single();
      if (fallbackErr) {
        console.error('[studio_production] studio line insert failed', fallbackErr);
        throw new Error(`Failed to add studio product line: ${fallbackErr.message}. Add studio line from Studio Sale Detail.`);
      }
      const insertedItemId = (fallbackData as any)?.id;
      await this.setGeneratedInvoiceItem(productionId, product.id, insertedItemId);
      if (import.meta.env?.DEV) {
        console.log('[studio_production] studio line added (sales_items fallback), links saved', { generatedProductId: product.id, generatedInvoiceItemId: insertedItemId });
      }
      return { generatedProductId: product.id, generatedInvoiceItemId: insertedItemId };
    }
    const insertedItemId = (insertedItem as any)?.id;
    await this.setGeneratedInvoiceItem(productionId, product.id, insertedItemId);
    if (import.meta.env?.DEV) {
      console.log('[studio_production] studio line added (sales_items), links saved', { generatedProductId: product.id, generatedInvoiceItemId: insertedItemId });
    }
    return { generatedProductId: product.id, generatedInvoiceItemId: insertedItemId };
  },

  async createProductionJob(input: CreateProductionInput): Promise<StudioProduction> {
    if (!input.sale_id) throw new Error('Sale is required. Studio production must be linked to a sale.');
    const payload = {
      company_id: input.company_id,
      branch_id: input.branch_id,
      sale_id: input.sale_id,
      production_no: input.production_no,
      production_date: input.production_date,
      product_id: input.product_id,
      variation_id: input.variation_id ?? null,
      quantity: input.quantity,
      boxes: input.boxes ?? null,
      pieces: input.pieces ?? null,
      unit: input.unit ?? 'piece',
      estimated_cost: input.estimated_cost ?? 0,
      actual_cost: input.actual_cost ?? 0,
      status: 'draft' as const,
      start_date: input.start_date ?? null,
      expected_date: input.expected_date ?? null,
      assigned_worker_id: input.assigned_worker_id ?? null,
      assigned_machine_or_karigar: input.assigned_machine_or_karigar ?? null,
      notes: input.notes ?? null,
      instructions: input.instructions ?? null,
      created_by: input.created_by ?? null,
    };

    let { data, error } = await supabase
      .from('studio_productions')
      .insert(payload)
      .select('*')
      .single();

    if (error) throw error;
    if (data?.product_id) {
      const { data: prod } = await supabase.from('products').select('id, name, sku').eq('id', data.product_id).maybeSingle();
      if (prod) (data as any).product = prod;
    }

    await logAction(
      data.id,
      'created',
      null,
      payload as unknown as Record<string, unknown>,
      input.created_by ?? null
    ).then(({ error: logErr }) => { if (logErr) console.warn('[studio_production_logs]', logErr); });

    return data as StudioProduction;
  },

  async updateProductionJob(
    id: string,
    updates: Partial<Omit<StudioProduction, 'id' | 'company_id' | 'production_no' | 'created_at'>>,
    performedBy?: string | null
  ): Promise<StudioProduction> {
    const existing = await this.getProductionById(id);
    if (!existing) throw new Error('Production not found');
    if (existing.status === 'completed') throw new Error('Completed production cannot be edited (admin override not implemented)');

    const allowed: Record<string, unknown> = {};
    const keys = [
      'production_date', 'product_id', 'variation_id', 'quantity', 'boxes', 'pieces', 'unit',
      'estimated_cost', 'actual_cost', 'start_date', 'expected_date', 'assigned_worker_id',
      'assigned_machine_or_karigar', 'notes', 'instructions',
    ] as const;
    keys.forEach(k => { if (updates[k] !== undefined) allowed[k] = updates[k]; });

    const { data, error } = await supabase
      .from('studio_productions')
      .update(allowed)
      .eq('id', id)
      .select(`
        *,
        product:products!product_id(id, name, sku),
        worker:workers(id, name)
      `)
      .single();

    if (error) throw error;

    await logAction(
      id,
      'updated',
      existing as unknown as Record<string, unknown>,
      allowed,
      performedBy ?? null
    ).then(({ error: logErr }) => { if (logErr) console.warn('[studio_production_logs]', logErr); });

    return data as StudioProduction;
  },

  async changeProductionStatus(
    id: string,
    newStatus: StudioProductionStatus,
    performedBy?: string | null
  ): Promise<StudioProduction> {
    const existing = await this.getProductionById(id);
    if (!existing) throw new Error('Production not found');

    const oldStatus = existing.status;
    if (oldStatus === newStatus) return existing;

    if (newStatus === 'completed') {
      if (oldStatus === 'cancelled') throw new Error('Cancelled production cannot be completed');
      const saleId = existing.sale_id;
      if (!saleId) throw new Error('Production must be linked to a sale to complete. Link this production to a sale first.');
      if (!(existing as any).generated_invoice_item_id) {
        throw new Error(
          'Generate the studio invoice (Product & Invoice) before completing production. Worker costs and inventory post after the bill is linked.'
        );
      }

      await postPendingStageCostsAndLedgersForProduction(
        id,
        this.ensureWorkerLedgerEntryForStage.bind(this),
        performedBy ?? null
      );

      const stages = await this.getStagesByProductionId(id);
      let studioCharges = 0;
      if (stages.length > 0) {
        const notCompleted = stages.filter(s => (s as any).status !== 'completed');
        if (notCompleted.length > 0) throw new Error('All production stages must be completed before completing production.');
        studioCharges = stages.reduce((sum, s) => sum + (Number((s as any).cost) || 0), 0);
      }

      const { data: saleRow, error: saleFetchErr } = await supabase
        .from('sales')
        .select('id, total, paid_amount, company_id, order_no, invoice_no')
        .eq('id', saleId)
        .single();
      if (saleFetchErr || !saleRow) throw new Error('Linked sale not found. Cannot complete production.');
      const companyIdForLedger = (saleRow as any).company_id || existing.company_id;

      // Order: ledger first, then inventory, then sale, then production status (so failure doesn't leave inconsistent state).
      // 1. Worker ledger (idempotent: skip if entry already exists for this stage).
      for (const stage of stages) {
        const workerId = (stage as any).assigned_worker_id;
        const amount = Number((stage as any).cost) || 0;
        if (workerId && amount > 0) {
          const { data: existingLedger } = await supabase
            .from('worker_ledger_entries')
            .select('id')
            .eq('reference_type', 'studio_production_stage')
            .eq('reference_id', (stage as any).id)
            .limit(1)
            .maybeSingle();
          if (!existingLedger) {
            let jobRef: string | null = null;
            try {
              jobRef = await settingsService.getNextDocumentNumber(
                companyIdForLedger,
                existing.branch_id || undefined,
                'job'
              );
            } catch (e) {
              console.warn('[studioProductionService] getNextDocumentNumber(job) failed:', e);
            }
            const insertPayload: Record<string, unknown> = {
              company_id: companyIdForLedger,
              worker_id: workerId,
              amount,
              reference_type: 'studio_production_stage',
              reference_id: (stage as any).id,
              notes: `Studio production ${existing.production_no} – stage completed`,
              ...(jobRef ? { document_no: jobRef } : {}),
            };
            const { error: ledgerErr } = await supabase.from('worker_ledger_entries').insert(insertPayload);
            if (ledgerErr) throw new Error(`Worker ledger failed: ${ledgerErr.message}`);
          }
        }
      }

      // 2. Inventory — single PRODUCTION_IN (+1) via studioStockLifecycleService (stage-cost basis)
      try {
        const { ensureStudioProductionInForSale } = await import('./studioStockLifecycleService');
        await ensureStudioProductionInForSale(saleId);
      } catch (invErr: unknown) {
        console.warn('[studioProductionService] ensureStudioProductionInForSale:', invErr);
      }

      // 3. Sale: studio_charges (metadata) and due_amount
      // If generated invoice item exists, studio product line is already in sales.total — don't add studioCharges again
      const hasGeneratedItem = !!(existing as any).generated_invoice_item_id;
      const currentTotal = Number(saleRow.total) || 0;
      const paidAmount = Number((saleRow as any).paid_amount) || 0;
      const dueAmount = hasGeneratedItem
        ? Math.max(0, currentTotal - paidAmount) // studio line already in total
        : Math.max(0, currentTotal + studioCharges - paidAmount); // legacy: no studio line in items
      const saleUpdatePayload: Record<string, unknown> = {
        studio_charges: studioCharges, // metadata only (production cost for reports)
        due_amount: dueAmount,
        status: 'final',
      };
      // Studio sales store their number in order_no — copy to invoice_no for customer ledger visibility
      if (!(saleRow as any).invoice_no) {
        const orderNo = (saleRow as any).order_no;
        if (orderNo) saleUpdatePayload.invoice_no = orderNo;
      }
      const { error: saleUpdateErr } = await supabase
        .from('sales')
        .update(saleUpdatePayload)
        .eq('id', saleId);
      if (saleUpdateErr) throw new Error(`Failed to update sale with studio charges: ${saleUpdateErr.message}`);

      // Update generated product cost_price for COGS calculation
      if ((existing as any).generated_product_id && studioCharges > 0) {
        await supabase
          .from('products')
          .update({ cost_price: studioCharges })
          .eq('id', (existing as any).generated_product_id);
      }

      // 4. Create sale accounting JE (Dr AR / Cr Revenue / Dr COGS / Cr Inventory)
      // Studio sales become 'final' here but SalesContext never triggers JE for this path
      try {
        const { postSaleDocumentAccounting } = await import('./documentPostingEngine');
        const jeId = await postSaleDocumentAccounting(saleId);
        if (jeId) console.log('[studioProductionService] Sale JE created:', jeId);
      } catch (jeErr: any) {
        console.warn('[studioProductionService] Sale JE creation failed (non-fatal):', jeErr?.message);
      }
    }
    // cancelled: no inventory impact

    const updatePayload: Record<string, unknown> = { status: newStatus };
    if (newStatus === 'completed') updatePayload.completed_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('studio_productions')
      .update(updatePayload)
      .eq('id', id)
      .select(`
        *,
        product:products!product_id(id, name, sku),
        worker:workers(id, name)
      `)
      .single();

    if (error) throw error;

    await logAction(
      id,
      'status_changed',
      { status: oldStatus },
      { status: newStatus, completed_at: (updatePayload as any).completed_at },
      performedBy ?? null
    ).then(({ error: logErr }) => { if (logErr) console.warn('[studio_production_logs]', logErr); });

    return data as StudioProduction;
  },

  async deleteProductionJob(id: string): Promise<void> {
    const existing = await this.getProductionById(id);
    if (!existing) throw new Error('Production not found');
    if (existing.status !== 'draft') throw new Error('Only draft productions can be deleted');
    const { error } = await supabase.from('studio_productions').delete().eq('id', id);
    if (error) throw error;
  },

  async getProductionLogs(productionId: string): Promise<StudioProductionLog[]> {
    const { data, error } = await supabase
      .from('studio_production_logs')
      .select('*')
      .eq('production_id', productionId)
      .order('performed_at', { ascending: false });

    if (error) {
      if (error.code === 'PGRST116' || error.message?.includes('does not exist')) return [];
      throw error;
    }
    return (data || []) as StudioProductionLog[];
  },

  /** Batch: get stages for multiple productions in one query. Returns map productionId -> stages[]. */
  async getStagesByProductionIds(productionIds: string[]): Promise<Map<string, StudioProductionStage[]>> {
    const out = new Map<string, StudioProductionStage[]>();
    if (productionIds.length === 0) return out;
    const uniq = [...new Set(productionIds)].filter(Boolean);
    try {
      const { data, error } = await supabase
        .from('studio_production_stages')
        .select('*')
        .in('production_id', uniq)
        .order('created_at', { ascending: true });
      if (error) {
        if (error.code === 'PGRST116' || error.message?.includes('does not exist')) return out;
        throw error;
      }
      const rows = (data || []) as any[];
      const workerIds = [...new Set(rows.filter((s) => s.assigned_worker_id).map((s) => s.assigned_worker_id))];
      const nameById = workerIds.length > 0 ? await workerDisplayNamesByIds(workerIds) : new Map<string, string>();
      rows.forEach((s) => {
        const pid = s.production_id;
        if (!out.has(pid)) out.set(pid, []);
        if (s.assigned_worker_id) {
          s.worker = { id: s.assigned_worker_id, name: nameById.get(s.assigned_worker_id) || '' };
        }
        out.get(pid)!.push(s as StudioProductionStage);
      });
      return out;
    } catch (e: any) {
      if (e?.code === '42P01' || e?.message?.includes('does not exist')) return out;
      throw e;
    }
  },

  /** Stages for a production. Resolve worker display names from contacts, then workers (mobile parity). */
  async getStagesByProductionId(productionId: string): Promise<StudioProductionStage[]> {
    try {
      const { data, error } = await supabase
        .from('studio_production_stages')
        .select('*')
        .eq('production_id', productionId)
        .order('stage_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) {
        if (import.meta.env?.DEV) console.log('[studioProductionService] getStagesByProductionId error:', { productionId, code: error.code, message: error.message });
        if (error.code === 'PGRST116' || error.message?.includes('does not exist')) return [];
        throw error;
      }
      const stages = (data || []) as any[];
      if (import.meta.env?.DEV && stages.length > 0) console.log('[studioProductionService] getStagesByProductionId', { productionId, count: stages.length });
      const workerIds = stages.filter((s) => s.assigned_worker_id).map((s) => s.assigned_worker_id);
      if (workerIds.length > 0) {
        const nameById = await workerDisplayNamesByIds([...new Set(workerIds)]);
        stages.forEach((s) => {
          if (s.assigned_worker_id) {
            const name = nameById.get(s.assigned_worker_id) || '';
            s.worker = { id: s.assigned_worker_id, name };
          }
        });
      }
      return stages as StudioProductionStage[];
    } catch (e: any) {
      if (e?.code === '42P01' || e?.message?.includes('does not exist')) return [];
      throw e;
    }
  },

  /**
   * Z2: Paid / partial / unpaid per stage — uses `payments` (worker_payment) + FIFO + worker_ledger when companyId set.
   * Legacy: worker_ledger rows only when companyId omitted.
   */
  async getStageWorkerPaymentDetails(
    companyId: string,
    stageIds: string[],
    branchId?: string | null
  ): Promise<Record<string, StageWorkerPaymentDetail>> {
    if (stageIds.length === 0) return {};
    try {
      return await fetchStageWorkerPaymentDetailsImpl(companyId, stageIds, branchId);
    } catch {
      return Object.fromEntries(stageIds.map((id) => [id, { status: 'unpaid' as const, paidAmount: 0, remainingDue: 0 }]));
    }
  },

  /** Ledger status per stage (for Payable vs Paid vs Partial). Pass companyId for Accounting worker payments visibility. */
  async getLedgerStatusForStages(
    stageIds: string[],
    companyId?: string | null,
    branchId?: string | null
  ): Promise<Record<string, 'unpaid' | 'partial' | 'paid'>> {
    if (stageIds.length === 0) return {};
    if (companyId) {
      try {
        const det = await fetchStageWorkerPaymentDetailsImpl(companyId, stageIds, branchId);
        const out: Record<string, 'unpaid' | 'partial' | 'paid'> = {};
        stageIds.forEach((id) => {
          out[id] = det[id]?.status ?? 'unpaid';
        });
        return out;
      } catch {
        /* fall through to ledger-only */
      }
    }
    try {
      const { data, error } = await supabase
        .from('worker_ledger_entries')
        .select('reference_id, status')
        .eq('reference_type', 'studio_production_stage')
        .in('reference_id', stageIds);
      if (error) return {}; // e.g. column "status" does not exist before migration
      const byStage: Record<string, { paid: number; unpaid: number }> = {};
      (data || []).forEach((row: { reference_id: string; status?: string }) => {
        const sid = row.reference_id;
        if (!byStage[sid]) byStage[sid] = { paid: 0, unpaid: 0 };
        const st = (row.status || 'unpaid').toLowerCase();
        if (st === 'paid') byStage[sid].paid++;
        else byStage[sid].unpaid++;
      });
      const out: Record<string, 'unpaid' | 'partial' | 'paid'> = {};
      Object.entries(byStage).forEach(([sid, counts]) => {
        if (counts.unpaid === 0) out[sid] = 'paid';
        else if (counts.paid === 0) out[sid] = 'unpaid';
        else out[sid] = 'partial';
      });
      return out;
    } catch {
      return {};
    }
  },

  /** Create a stage (process step) for a production. PHASE 1: No auto-assignment – status=pending, cost=0, assigned_worker_id=null. Assignment ONLY via Assign flow (RPC). position = stage_order (1-based); omit to default to 1. */
  async createStage(
    productionId: string,
    input: {
      stage_type: StudioProductionStageType;
      assigned_worker_id?: string | null;
      cost?: number;
      expected_completion_date?: string | null;
      notes?: string | null;
    },
    position?: number
  ): Promise<StudioProductionStage> {
    const production = await this.getProductionById(productionId);
    if (!production) throw new Error('Production not found');
    if (!production.sale_id) throw new Error('Production must be linked to a sale to add a stage.');
    // GUARD: Never use input.assigned_worker_id on create. Worker assignment only via assignWorkerToStage / RPC.
    if (input.assigned_worker_id) {
      console.warn('[studioProductionService] createStage: ignoring assigned_worker_id – assignment only via Assign flow');
    }
    const insertPayload: Record<string, unknown> = {
      production_id: productionId,
      stage_type: input.stage_type,
      assigned_worker_id: null,
      cost: 0,
      expected_completion_date: input.expected_completion_date || null,
      status: 'pending',
      notes: input.notes ?? null,
      expected_cost: 0,
    };
    if (position != null) insertPayload.stage_order = position;
    let result = await supabase
      .from('studio_production_stages')
      .insert(insertPayload)
      .select('*')
      .single();
    if (result.error && (result.error.message?.includes('expected_cost') || result.error.message?.includes('schema cache'))) {
      delete insertPayload.expected_cost;
      result = await supabase
        .from('studio_production_stages')
        .insert(insertPayload)
        .select('*')
        .single();
    }
    if (result.error) {
      if (import.meta.env?.DEV) console.warn('[studioProductionService] createStage failed:', { productionId, stage_type: input.stage_type, message: result.error.message });
      throw result.error;
    }
    const data = result.data as any;
    if (import.meta.env?.DEV) console.log('[studioProductionService] createStage ok', { productionId, stageId: data?.id, stage_type: input.stage_type });
    if (data?.assigned_worker_id) {
      const stages = await this.getStagesByProductionId(productionId);
      const found = stages.find((s) => s.id === data.id);
      if (found?.worker) data.worker = found.worker;
    }
    return data as StudioProductionStage;
  },

  /**
   * Replace all stages for a production: DELETE existing then INSERT all in order.
   * Use when user saves "Customize Tasks" and all stages are pending (no workers assigned).
   * Ensures correct stage_order (position) so multiple stages save; single createStage defaults to stage_order=1 and would conflict.
   */
  async replaceStages(
    productionId: string,
    stages: { stage_type: StudioProductionStageType; position: number; notes?: string | null }[]
  ): Promise<StudioProductionStage[]> {
    if (stages.length === 0) {
      throw new Error('At least one stage is required.');
    }
    const production = await this.getProductionById(productionId);
    if (!production) throw new Error('Production not found');
    if (!production.sale_id) throw new Error('Production must be linked to a sale.');

    const { error: delErr } = await supabase
      .from('studio_production_stages')
      .delete()
      .eq('production_id', productionId);
    if (delErr) {
      if (import.meta.env?.DEV) console.warn('[studioProductionService] replaceStages delete failed:', { productionId, message: delErr.message, code: delErr.code });
      throw delErr;
    }

    const rows = stages.map((s) => ({
      production_id: productionId,
      stage_type: s.stage_type,
      stage_order: s.position,
      assigned_worker_id: null,
      cost: 0,
      status: 'pending',
      notes: s.notes ?? null,
      expected_cost: 0,
    }));
    const { data: inserted, error: insErr } = await supabase
      .from('studio_production_stages')
      .insert(rows)
      .select('*');
    if (insErr) {
      if (import.meta.env?.DEV) {
        console.warn('[studioProductionService] replaceStages insert failed (stage_order may not exist):', { productionId, message: insErr.message });
      }
      const rowsWithoutOrder = stages.map((s) => ({
        production_id: productionId,
        stage_type: s.stage_type,
        assigned_worker_id: null,
        cost: 0,
        status: 'pending',
        notes: s.notes ?? null,
      }));
      // Retry delete (RLS may have blocked first delete) then insert to avoid 409
      const { error: del2Err } = await supabase.from('studio_production_stages').delete().eq('production_id', productionId);
      if (del2Err && import.meta.env?.DEV) console.warn('[studioProductionService] replaceStages retry delete:', del2Err.message);
      const { data: fallback, error: fallbackErr } = await supabase
        .from('studio_production_stages')
        .insert(rowsWithoutOrder)
        .select('*');
      if (fallbackErr) {
        if (fallbackErr.code === '23505' || (fallbackErr.message || '').includes('409')) {
          throw new Error(
            'Stages could not be saved: existing rows conflict. Ensure studio_production_stages has stage_order column (run migrations/fix_after_drop_studio_orders.sql) and RLS allows delete/insert.'
          );
        }
        throw fallbackErr;
      }
      if (import.meta.env?.DEV) console.log('[studioProductionService] replaceStages fallback (no stage_order)', { productionId, count: (fallback || []).length });
      return (fallback || []) as StudioProductionStage[];
    }
    if (import.meta.env?.DEV) console.log('[studioProductionService] replaceStages ok', { productionId, count: (inserted || []).length });
    return (inserted || []) as StudioProductionStage[];
  },

  /** Delete a stage before send/receive. Allowed for pending or assigned (no paid worker ledger). */
  async deleteStage(stageId: string): Promise<void> {
    const { data: existing } = await supabase
      .from('studio_production_stages')
      .select('id, status, stage_type, assigned_worker_id, sent_date')
      .eq('id', stageId)
      .single();
    if (!existing) throw new Error('Stage not found');
    const row = existing as {
      status?: string;
      stage_type?: string;
      assigned_worker_id?: string | null;
      sent_date?: string | null;
    };
    if (row.status === 'completed') {
      throw new Error('Cannot remove a completed stage. Reopen it first if needed.');
    }
    const blocked = ['sent_to_worker', 'received', 'completed', 'in_progress'];
    if (blocked.includes(String(row.status || '')) || row.sent_date) {
      throw new Error(
        'Cannot remove a stage after work has been sent to the worker. Reopen or complete cleanup first.',
      );
    }
    const deletableStatuses = ['pending', 'assigned'];
    if (!deletableStatuses.includes(String(row.status || ''))) {
      throw new Error('This stage cannot be removed in its current status.');
    }
    const { data: ledgerRows } = await supabase
      .from('worker_ledger_entries')
      .select('id, status')
      .eq('reference_type', 'studio_production_stage')
      .eq('reference_id', stageId);
    const paid = (ledgerRows || []).some(
      (r: { status?: string }) => String(r.status || '').toLowerCase() === 'paid',
    );
    if (paid) {
      throw new Error('Cannot remove a stage that has been paid. Reverse payment in accounting first.');
    }
    if ((ledgerRows || []).length > 0) {
      await supabase
        .from('worker_ledger_entries')
        .delete()
        .eq('reference_type', 'studio_production_stage')
        .eq('reference_id', stageId);
    }
    const { error } = await supabase.from('studio_production_stages').delete().eq('id', stageId);
    if (error) throw error;
  },

  /** PHASE 2: Assign worker to stage. Uses RPC when available (authoritative). Saves assigned_worker_id, assigned_at, expected_cost; status → assigned. No journal entry. Step B: worker and expected_completion_date are mandatory. */
  async assignWorkerToStage(
    stageId: string,
    params: { worker_id: string; expected_cost: number; expected_completion_date?: string | null; notes?: string | null }
  ): Promise<StudioProductionStage> {
    await assertProductionPipelineUnlockedForStage(stageId);
    if (!params.worker_id?.trim()) throw new Error('Worker is required to assign this step.');
    const expectedDate = params.expected_completion_date?.trim() || null;
    if (!expectedDate) throw new Error('Expected completion date is required to assign this step.');
    const companyId = await companyIdForStage(stageId);
    if (!companyId) throw new Error('Production not found for this stage.');
    const workerId = await ensureWorkerRowForAssign(companyId, params.worker_id);
    const { data: rpcResult, error: rpcErr } = await supabase.rpc('rpc_assign_worker_to_stage', {
      p_stage_id: stageId,
      p_worker_id: workerId,
      p_expected_cost: params.expected_cost,
      p_expected_completion_date: expectedDate,
      p_notes: params.notes ?? null,
    });
    if (!rpcErr && rpcResult?.ok) {
      const { data: stageRow } = await supabase.from('studio_production_stages').select('production_id').eq('id', stageId).single();
      if (stageRow?.production_id) {
        const stages = await this.getStagesByProductionId((stageRow as any).production_id);
        const updated = stages.find((s) => s.id === stageId);
        if (updated) return updated;
      }
    }
    if (rpcErr?.code === '42883' || rpcErr?.message?.includes('function') || rpcErr?.message?.includes('does not exist')) {
      // RPC not deployed – fallback to direct update (status=assigned or in_progress)
      const { data: existing } = await supabase
        .from('studio_production_stages')
        .select('id, status')
        .eq('id', stageId)
        .single();
      if (!existing) throw new Error('Stage not found');
      if ((existing as any).status === 'completed') throw new Error('Cannot assign worker to a completed stage.');
      const updatePayload: Record<string, unknown> = {
        assigned_worker_id: workerId,
        assigned_at: new Date().toISOString(),
        expected_cost: params.expected_cost,
        cost: params.expected_cost,
        status: 'assigned',
        expected_completion_date: expectedDate,
        ...(params.notes != null ? { notes: params.notes } : {}),
      };
      let result = await supabase
        .from('studio_production_stages')
        .update(updatePayload)
        .eq('id', stageId)
        .select('*')
        .single();
      if (result.error && (result.error.message?.includes('expected_cost') || result.error.message?.includes('assigned_at') || result.error.message?.includes('assigned'))) {
        updatePayload.status = 'in_progress';
        delete (updatePayload as any).expected_cost;
        delete (updatePayload as any).assigned_at;
        result = await supabase.from('studio_production_stages').update(updatePayload).eq('id', stageId).select('*').single();
      }
      if (result.error) throw result.error;
      const row = result.data as any;
      await resolveStageWorker(row);
      return row as StudioProductionStage;
    }
    if (rpcErr) throw new Error(rpcResult?.error ?? rpcErr.message);
    const { data: stageRow } = await supabase.from('studio_production_stages').select('production_id').eq('id', stageId).single();
    if (stageRow?.production_id) {
      const stages = await this.getStagesByProductionId((stageRow as any).production_id);
      const updated = stages.find((s) => s.id === stageId);
      if (updated) return updated;
    }
    throw new Error('Stage not found after assign');
  },

  /** Receive work: sent_to_worker → received (optional date + receive note). */
  async receiveWork(stageId: string, opts?: { receivedDate?: string | null; notes?: string | null }): Promise<void> {
    await assertProductionPipelineUnlockedForStage(stageId);
    const p_received_date = parseStudioWorkflowDate(opts?.receivedDate ?? undefined);
    const noteText = (opts?.notes ?? '').trim();
    const { data: r, error: e } = await supabase.rpc('rpc_receive_work', {
      p_stage_id: stageId,
      ...(p_received_date ? { p_received_date } : {}),
      ...(noteText ? { p_notes: noteText } : {}),
    });
    if (e) throw new Error((r as { error?: string })?.error ?? e.message);
    if (!(r as { ok?: boolean })?.ok) {
      throw new Error((r as { error?: string })?.error ?? 'Receive work failed');
    }
  },

  /** Send to worker: assigned → sent_to_worker (optional backdated sent_date). */
  async sendToWorker(stageId: string, opts?: { sentDate?: string | null; notes?: string | null }): Promise<void> {
    await assertProductionPipelineUnlockedForStage(stageId);
    const p_sent_date = parseStudioWorkflowDate(opts?.sentDate ?? undefined);
    const noteText = (opts?.notes ?? '').trim();
    const { data: r, error: e } = await supabase.rpc('rpc_send_to_worker', {
      p_stage_id: stageId,
      ...(p_sent_date ? { p_sent_date } : {}),
      ...(noteText ? { p_notes: noteText } : {}),
    });
    if (e) throw new Error((r as { error?: string })?.error ?? e.message);
    if (!(r as { ok?: boolean })?.ok) {
      throw new Error((r as { error?: string })?.error ?? 'Send to worker failed');
    }
  },

  /** Update stage (worker, cost, status, etc.). Completed stages: only notes allowed (cost lock). */
  /**
   * Legacy hook (pre–post-bill): used to backfill worker_ledger from completed stages.
   * Worker ledger rows are created only after studio invoice + production completion (backflush). No-op.
   */
  async syncWorkerLedgerEntriesForProduction(_productionId: string): Promise<void> {
    return;
  },

  /**
   * Ensure worker_ledger_entry exists for a completed stage (when saved via persist, not Receive).
   * Called from updateStage when marking stage completed with cost + worker.
   */
  async ensureWorkerLedgerEntryForStage(stageId: string, cost: number, workerId: string): Promise<void> {
    if (!workerId || cost <= 0) return;
    const { data: existing } = await supabase
      .from('worker_ledger_entries')
      .select('id')
      .eq('reference_type', 'studio_production_stage')
      .eq('reference_id', stageId)
      .limit(1)
      .maybeSingle();
    if (existing) return;

    const { data: stageRow } = await supabase
      .from('studio_production_stages')
      .select('production_id')
      .eq('id', stageId)
      .single();
    if (!stageRow) return;
    const { data: prodRow } = await supabase
      .from('studio_productions')
      .select('id, company_id, branch_id, production_no')
      .eq('id', (stageRow as any).production_id)
      .single();
    if (!prodRow) return;
    const production = prodRow as any;

    let jobRef: string | null = null;
    try {
      jobRef = await settingsService.getNextDocumentNumber(
        production.company_id,
        production.branch_id || undefined,
        'job'
      );
    } catch {
      /* ignore */
    }
    const insertPayload: Record<string, unknown> = {
      company_id: production.company_id,
      worker_id: workerId,
      amount: cost,
      reference_type: 'studio_production_stage',
      reference_id: stageId,
      notes: `Studio production ${production.production_no} – stage completed`,
      status: 'unpaid',
      ...(jobRef ? { document_no: jobRef } : {}),
    };
    const { error: ledgerErr } = await supabase.from('worker_ledger_entries').insert(insertPayload);
    if (ledgerErr) {
      console.warn('[studioProductionService] ensureWorkerLedgerEntry failed:', ledgerErr.message);
      return;
    }
    // P1-3: balance is derived from GL — do not write workers.current_balance
  },

  async updateStage(
    stageId: string,
    updates: {
      status?: StudioProductionStageStatus;
      cost?: number;
      completed_at?: string | null;
      notes?: string | null;
      assigned_worker_id?: string | null;
      expected_completion_date?: string | null;
    }
  ): Promise<StudioProductionStage> {
    const { data: existingRow } = await supabase.from('studio_production_stages').select('status, production_id, stage_type').eq('id', stageId).single();
    if (!existingRow) throw new Error('Stage not found');
    const isCompleted = (existingRow as any).status === 'completed';
    const isReopening = isCompleted && updates.status === 'in_progress';
    if (isCompleted && !isReopening) {
      if (updates.status !== undefined && updates.status !== 'completed') throw new Error('Completed stage cannot be reopened.');
      if (updates.completed_at !== undefined) throw new Error('Completed stage cannot be edited.');
      if (updates.assigned_worker_id !== undefined) throw new Error('Completed stage worker is locked.');
      // Allow cost correction on completed stage (and notes); sync sale totals via trigger
      if (updates.cost !== undefined || updates.notes !== undefined) {
        const payload: Record<string, unknown> = {};
        if (updates.cost !== undefined) payload.cost = updates.cost;
        if (updates.notes !== undefined) payload.notes = updates.notes;
        if (Object.keys(payload).length > 0) {
          const { data: updated, error } = await supabase
            .from('studio_production_stages')
            .update(payload)
            .eq('id', stageId)
            .select('*')
            .single();
          if (error) throw error;
          const result = updated as StudioProductionStage;
          await resolveStageWorker(result);
          return result;
        }
      }
      if (updates.notes === undefined && updates.cost === undefined) {
        const { data: d } = await supabase.from('studio_production_stages').select('*').eq('id', stageId).single();
        if (d) {
          const row = d as any;
          if (row.assigned_worker_id) {
            const { data: c } = await supabase.from('contacts').select('id, name').eq('id', row.assigned_worker_id).maybeSingle();
            if (c) row.worker = { id: row.assigned_worker_id, name: (c as any).name || '' };
          }
          return row as StudioProductionStage;
        }
        throw new Error('Stage not found');
      }
    }

    // Post-bill model: completing a stage is operational only (cost/worker). JE + worker_ledger post when bill finalizes (see postPendingStageCostsAndLedgersForProduction).
    if (updates.status === 'completed') {
      const cost = Number(updates.cost ?? 0) || 0;
      const workerId = updates.assigned_worker_id ?? null;
      if (cost > 0 && !workerId)
        throw new Error('Assign a worker before marking this stage complete (required for accounting).');
      const completedAt = updates.completed_at ?? new Date().toISOString();
      const payload: Record<string, unknown> = {
        status: 'completed',
        completed_at: completedAt,
        cost,
        journal_entry_id: null,
        notes: updates.notes ?? null,
        ...(updates.assigned_worker_id !== undefined ? { assigned_worker_id: updates.assigned_worker_id } : {}),
      };
      const { data: updated, error } = await supabase
        .from('studio_production_stages')
        .update(payload)
        .eq('id', stageId)
        .select('*')
        .single();
      if (error) throw error;
      const result = updated as StudioProductionStage;
      await resolveStageWorker(result);
      return result;
    }

    // Step B: When setting status to 'assigned', worker and expected completion date are required.
    if (updates.status === 'assigned') {
      if (!(updates.assigned_worker_id?.trim())) throw new Error('Worker is required when assigning this step.');
      if (!(updates.expected_completion_date?.trim())) throw new Error('Expected completion date is required when assigning this step.');
    }

    const payload: Record<string, unknown> = {};
    if (updates.status !== undefined) payload.status = updates.status;
    if (updates.cost !== undefined) payload.cost = updates.cost;
    if (updates.completed_at !== undefined) payload.completed_at = updates.completed_at;
    if (updates.notes !== undefined) payload.notes = updates.notes;
    if (updates.assigned_worker_id !== undefined) payload.assigned_worker_id = updates.assigned_worker_id;
    if (updates.expected_completion_date !== undefined) payload.expected_completion_date = updates.expected_completion_date;
    if (Object.keys(payload).length === 0) {
      const existing = await supabase.from('studio_production_stages').select('*').eq('id', stageId).single();
      if (existing.data) return existing.data as StudioProductionStage;
      throw new Error('Stage not found');
    }
    const { data, error } = await supabase
      .from('studio_production_stages')
      .update(payload)
      .eq('id', stageId)
      .select('*')
      .single();
    if (error) throw error;
    const row = data as any;
    await resolveStageWorker(row);
    return row as StudioProductionStage;
  },

  /**
   * Receive from worker: mark stage completed with actual cost. Uses RPC when available (authoritative).
   * Creates journal (Dr Expense, Cr Payable) and worker ledger. workerIdFromUI: use when DB has no assigned_worker_id.
   * Returns { stage, ledgerEntryId } so UI can show "Pay Now?" and record payment against this entry.
   */
  async receiveStage(
    stageId: string,
    actualCost: number,
    notes?: string | null,
    workerIdFromUI?: string | null,
    opts?: { receivedDate?: string | null }
  ): Promise<{ stage: StudioProductionStage; ledgerEntryId: string | null }> {
    await assertProductionPipelineUnlockedForStage(stageId);
    const { data: stageRow, error: stageErr } = await supabase
      .from('studio_production_stages')
      .select('id, production_id, assigned_worker_id, status')
      .eq('id', stageId)
      .single();
    if (stageErr || !stageRow) throw new Error('Stage not found');
    const stage = stageRow as any;
    if (stage.status === 'completed') throw new Error('Stage already received/completed');

    const p_received_date = parseStudioWorkflowDate(opts?.receivedDate ?? undefined);
    if (stage.status === 'sent_to_worker') {
      const receiveNoteText = (notes ?? '').trim();
      const { data: recvRpc, error: recvErr } = await supabase.rpc('rpc_receive_work', {
        p_stage_id: stageId,
        ...(p_received_date ? { p_received_date } : {}),
        ...(receiveNoteText ? { p_notes: receiveNoteText } : {}),
      });
      if (recvErr) throw new Error((recvRpc as { error?: string })?.error ?? recvErr.message);
      if (!(recvRpc as { ok?: boolean })?.ok) {
        throw new Error((recvRpc as { error?: string })?.error ?? 'Receive work failed');
      }
    }

    const effectiveWorkerId = workerIdFromUI || stage.assigned_worker_id || null;
    if (actualCost > 0 && !effectiveWorkerId)
      throw new Error('Assign a worker to this task before receiving (required for accounting).');

    const { data: rpcResult, error: rpcErr } = await supabase.rpc('rpc_receive_stage_and_finalize', {
      p_stage_id: stageId,
      p_final_cost: actualCost,
      // Receive remarks already appended via rpc_receive_work when status was sent_to_worker
      p_notes: stage.status === 'sent_to_worker' ? null : notes ?? null,
    });
    if (!rpcErr && rpcResult?.ok) {
      const { data: stageRow2 } = await supabase.from('studio_production_stages').select('production_id').eq('id', stageId).single();
      if (stageRow2?.production_id) {
        const stages = await this.getStagesByProductionId((stageRow2 as any).production_id);
        const updated = stages.find((s) => s.id === stageId);
        if (updated) {
          let ledgerEntryId: string | null = null;
          const { data: ledgerRow } = await supabase
            .from('worker_ledger_entries')
            .select('id')
            .eq('reference_type', 'studio_production_stage')
            .eq('reference_id', stageId)
            .limit(1)
            .maybeSingle();
          if (ledgerRow) ledgerEntryId = (ledgerRow as any).id;
          return { stage: updated, ledgerEntryId };
        }
      }
    }
    if (rpcErr?.code === '42883' || rpcErr?.message?.includes('function') || rpcErr?.message?.includes('does not exist')) {
      const completedAt = p_received_date ?? new Date().toISOString();
      const updated = await this.updateStage(stageId, {
        status: 'completed',
        cost: actualCost,
        completed_at: completedAt,
        assigned_worker_id: effectiveWorkerId,
      });
      if (p_received_date) {
        await supabase
          .from('studio_production_stages')
          .update({ received_date: p_received_date })
          .eq('id', stageId);
      }
      const frag = (notes ?? '').trim();
      if (frag) {
        const { error: fragErr } = await supabase.rpc('rpc_append_receive_notes_fragment', {
          p_stage_id: stageId,
          p_notes: frag,
        });
        if (fragErr && import.meta.env?.DEV) {
          console.warn('[studioProductionService] rpc_append_receive_notes_fragment:', fragErr.message);
        }
      }
      let ledgerEntryId: string | null = null;
      const { data: ledgerRow } = await supabase
        .from('worker_ledger_entries')
        .select('id')
        .eq('reference_type', 'studio_production_stage')
        .eq('reference_id', stageId)
        .limit(1)
        .maybeSingle();
      if (ledgerRow) ledgerEntryId = (ledgerRow as any).id;
      return { stage: updated, ledgerEntryId };
    }
    if (rpcErr) throw new Error(rpcResult?.error ?? rpcErr.message);
    throw new Error('Stage not found after receive');
  },

  /** Confirm worker settlement on a received stage (Pay Now + optional customer_charge). */
  async confirmStagePayment(
    stageId: string,
    params: {
      final_cost: number;
      pay_now: boolean;
      payment_account_id?: string | null;
      notes?: string | null;
      customer_charge?: number | null;
    }
  ): Promise<void> {
    await assertProductionPipelineUnlockedForStage(stageId);
    const cc = params.customer_charge != null && params.customer_charge > 0 ? params.customer_charge : null;
    const { data: r, error: e } = await supabase.rpc('rpc_confirm_stage_payment', {
      p_stage_id: stageId,
      p_final_cost: params.final_cost,
      p_pay_now: params.pay_now,
      p_payment_account_id: params.pay_now && params.payment_account_id ? params.payment_account_id : null,
      p_notes: params.notes ?? null,
      ...(cc != null ? { p_customer_charge: cc } : {}),
    });
    if (e) throw new Error((r as { error?: string })?.error ?? e.message);
    if (!(r as { ok?: boolean })?.ok) {
      throw new Error((r as { error?: string })?.error ?? 'Confirm payment failed');
    }
  },

  /** Full stage detail for worker job sheet / timeline. */
  async getStageJobDetail(stageId: string): Promise<{
    id: string;
    stage_type: string;
    status: string;
    stage_order?: number | null;
    assigned_at?: string | null;
    sent_date?: string | null;
    received_date?: string | null;
    completed_at?: string | null;
    expected_completion_date?: string | null;
    cost: number;
    expected_cost?: number | null;
    customer_charge?: number | null;
    notes?: string | null;
    worker_name?: string;
    production_no?: string;
    sale_id?: string;
    customer_name?: string;
    invoice_no?: string;
    ledger_paid: number;
    ledger_due: number;
    ledger_status: string;
  } | null> {
    const { data: stage, error } = await supabase
      .from('studio_production_stages')
      .select(
        'id, production_id, stage_type, stage_order, status, assigned_worker_id, assigned_at, sent_date, received_date, completed_at, expected_completion_date, cost, expected_cost, customer_charge, notes'
      )
      .eq('id', stageId)
      .maybeSingle();
    if (error || !stage) return null;
    const s = stage as any;
    let worker_name: string | undefined;
    if (s.assigned_worker_id) {
      const nameById = await workerDisplayNamesByIds([s.assigned_worker_id]);
      worker_name = nameById.get(s.assigned_worker_id);
    }
    let production_no: string | undefined;
    let sale_id: string | undefined;
    let customer_name: string | undefined;
    let invoice_no: string | undefined;
    if (s.production_id) {
      const { data: prod } = await supabase
        .from('studio_productions')
        .select('production_no, sale_id, sale:sales(customer_name, invoice_no)')
        .eq('id', s.production_id)
        .maybeSingle();
      if (prod) {
        const p = prod as any;
        production_no = p.production_no;
        sale_id = p.sale_id;
        customer_name = p.sale?.customer_name;
        invoice_no = p.sale?.invoice_no;
      }
    }
    let ledger_paid = 0;
    let ledger_due = 0;
    let ledger_status = 'none';
    const { data: ledgerRows } = await supabase
      .from('worker_ledger_entries')
      .select('amount, status')
      .eq('reference_type', 'studio_production_stage')
      .eq('reference_id', stageId);
    for (const r of ledgerRows || []) {
      const row = r as any;
      const amt = Number(row.amount) || 0;
      const st = (row.status || '').toLowerCase();
      if (st === 'paid') ledger_paid += amt;
      else if (st !== 'cancelled') ledger_due += amt;
    }
    if (ledger_paid > 0 && ledger_due > 0) ledger_status = 'partial';
    else if (ledger_paid > 0) ledger_status = 'paid';
    else if (ledger_due > 0) ledger_status = 'unpaid';
    return {
      id: s.id,
      stage_type: s.stage_type,
      status: s.status,
      stage_order: s.stage_order,
      assigned_at: s.assigned_at,
      sent_date: s.sent_date,
      received_date: s.received_date,
      completed_at: s.completed_at,
      expected_completion_date: s.expected_completion_date,
      cost: Number(s.cost) || 0,
      expected_cost: s.expected_cost != null ? Number(s.expected_cost) : null,
      customer_charge: s.customer_charge != null ? Number(s.customer_charge) : null,
      notes: s.notes,
      worker_name,
      production_no,
      sale_id,
      customer_name,
      invoice_no,
      ledger_paid,
      ledger_due,
      ledger_status,
    };
  },

  /**
   * PHASE 4: Reopen a completed stage. Uses RPC when available (authoritative).
   * Reverses journal entry, removes worker ledger, resets stage to assigned (keeps worker + expected cost).
   */
  async reopenStage(stageId: string, _performedBy?: string | null): Promise<StudioProductionStage> {
    await assertProductionPipelineUnlockedForStage(stageId);
    const { data: rpcResult, error: rpcErr } = await supabase.rpc('rpc_reopen_stage', { p_stage_id: stageId });
    if (!rpcErr && rpcResult?.ok) {
      const { data: stageRow } = await supabase.from('studio_production_stages').select('production_id').eq('id', stageId).single();
      if (stageRow?.production_id) {
        const stages = await this.getStagesByProductionId((stageRow as any).production_id);
        const updated = stages.find((s) => s.id === stageId);
        if (updated) return updated;
      }
    }
    if (rpcErr?.code === '42883' || rpcErr?.message?.includes('function') || rpcErr?.message?.includes('does not exist')) {
      const { data: stageRow, error: stageErr } = await supabase
        .from('studio_production_stages')
        .select('id, production_id, status, cost, assigned_worker_id, stage_type, journal_entry_id')
        .eq('id', stageId)
        .single();
      if (stageErr || !stageRow) throw new Error('Stage not found');
      const stage = stageRow as any;
      if (stage.status !== 'completed') throw new Error('Only completed stages can be reopened.');
      const cost = Number(stage.cost) || 0;
      const { data: prodRow } = await supabase
        .from('studio_productions')
        .select('id, company_id, branch_id, production_no')
        .eq('id', stage.production_id)
        .single();
      if (!prodRow) throw new Error('Production not found');
      const production = prodRow as any;
      if (cost > 0 && stage.journal_entry_id) {
        await createProductionCostReversalEntry({
          companyId: production.company_id,
          branchId: production.branch_id || null,
          stageId,
          workerId: stage.assigned_worker_id ?? null,
          productionNo: production.production_no,
          amount: cost,
          stageType: stage.stage_type || 'stage',
          performedBy: _performedBy,
        });
      }
      const { data: ledgerRow } = await supabase
        .from('worker_ledger_entries')
        .select('id, worker_id, amount, status')
        .eq('reference_type', 'studio_production_stage')
        .eq('reference_id', stageId)
        .limit(1)
        .maybeSingle();
      if (ledgerRow) {
        const entry = ledgerRow as { id: string; worker_id: string; amount: number; status?: string };
        await supabase.from('worker_ledger_entries').delete().eq('id', entry.id);
      }
      const { data: updated, error: updateErr } = await supabase
        .from('studio_production_stages')
        .update({
          status: 'assigned',
          completed_at: null,
          cost: 0,
          journal_entry_id: null,
        })
        .eq('id', stageId)
        .select('*')
        .single();
      if (updateErr) throw updateErr;
      const row = updated as any;
      await resolveStageWorker(row);
      return row as StudioProductionStage;
    }
    if (rpcErr) throw new Error(rpcResult?.error ?? rpcErr.message);
    throw new Error('Stage not found after reopen');
  },

  /**
   * PHASE 5: Change cost after completion. Reverses old journal, creates new journal, updates stage + worker ledger.
   * Do NOT directly overwrite cost; use this method so accounting stays in sync.
   */
  async changeStageCostAfterComplete(
    stageId: string,
    newCost: number,
    performedBy?: string | null
  ): Promise<StudioProductionStage> {
    if (newCost < 0) throw new Error('Cost cannot be negative.');
    const { data: stageRow, error: stageErr } = await supabase
      .from('studio_production_stages')
      .select('id, production_id, status, cost, assigned_worker_id, stage_type, journal_entry_id')
      .eq('id', stageId)
      .single();
    if (stageErr || !stageRow) throw new Error('Stage not found');
    const stage = stageRow as any;
    if (stage.status !== 'completed') throw new Error('Only completed stages can have cost changed.');
    const oldCost = Number(stage.cost) || 0;
    if (oldCost === newCost) {
      const { data: d } = await supabase.from('studio_production_stages').select('*').eq('id', stageId).single();
      if (d) {
        await resolveStageWorker(d);
        return d as StudioProductionStage;
      }
      throw new Error('Stage not found');
    }
    if (!stage.journal_entry_id) {
      const { data: upd, error: upCostErr } = await supabase
        .from('studio_production_stages')
        .update({ cost: newCost })
        .eq('id', stageId)
        .select('*')
        .single();
      if (upCostErr) throw upCostErr;
      await logAction(
        stage.production_id,
        'stage_cost_changed',
        { stage_id: stageId, old_cost: oldCost },
        { stage_id: stageId, new_cost: newCost },
        performedBy ?? null
      ).then(({ error: logErr }) => { if (logErr) console.warn('[studio_production_logs]', logErr); });
      if (upd) {
        await resolveStageWorker(upd);
        return upd as StudioProductionStage;
      }
      throw new Error('Stage not found');
    }
    const { data: prodRow } = await supabase.from('studio_productions').select('id, company_id, branch_id, production_no').eq('id', stage.production_id).single();
    if (!prodRow) throw new Error('Production not found');
    const production = prodRow as any;
    await createProductionCostReversalEntry({
      companyId: production.company_id,
      branchId: production.branch_id || null,
      stageId,
      workerId: stage.assigned_worker_id ?? null,
      productionNo: production.production_no,
      amount: oldCost,
      stageType: stage.stage_type || 'stage',
      performedBy,
    });
    const journalId = await createProductionCostJournalEntry({
      companyId: production.company_id,
      branchId: production.branch_id || null,
      stageId,
      workerId: stage.assigned_worker_id,
      productionNo: production.production_no,
      saleId: null,
      amount: newCost,
      stageType: stage.stage_type || 'stage',
      performedBy,
    });
    await supabase.from('studio_production_stages').update({ cost: newCost, journal_entry_id: journalId }).eq('id', stageId);
    // PHASE 5: Log cost change history (production-level audit log)
    await logAction(
      stage.production_id,
      'stage_cost_changed',
      { stage_id: stageId, old_cost: oldCost },
      { stage_id: stageId, new_cost: newCost },
      performedBy ?? null
    ).then(({ error: logErr }) => { if (logErr) console.warn('[studio_production_logs]', logErr); });
    const { data: ledgerRow } = await supabase
      .from('worker_ledger_entries')
      .select('id, worker_id')
      .eq('reference_type', 'studio_production_stage')
      .eq('reference_id', stageId)
      .limit(1)
      .maybeSingle();
    if (ledgerRow && stage.assigned_worker_id) {
      await supabase.from('worker_ledger_entries').update({ amount: newCost }).eq('id', (ledgerRow as any).id);
      // P1-3: balance is derived from GL — do not write workers.current_balance
    }
    const { data: d } = await supabase.from('studio_production_stages').select('*').eq('id', stageId).single();
    if (d) {
      await resolveStageWorker(d);
      return d as StudioProductionStage;
    }
    throw new Error('Stage not found');
  },

  /**
   * Mark worker ledger entry for a stage as paid (after user records payment via Pay Now or Accounting).
   * Updates ledger status and decrements worker current_balance.
   * Phase-2: Job rows (studio_production_stage) must NOT carry PAY ref; only accounting_payment rows may.
   * We always set payment_reference to null here so job rows are not contaminated.
   */
  async markStageLedgerPaid(stageId: string, _paymentReference?: string | null): Promise<void> {
    const { data: ledgerRow, error: ledgerFindErr } = await supabase
      .from('worker_ledger_entries')
      .select('id, worker_id, amount, status')
      .eq('reference_type', 'studio_production_stage')
      .eq('reference_id', stageId)
      .limit(1)
      .maybeSingle();
    if (ledgerFindErr || !ledgerRow) {
      throw new Error(
        'No worker payable for this stage yet. Generate the studio invoice and complete production (or use Final Complete) so costs post to the ledger, then pay the worker.'
      );
    }
    const entry = ledgerRow as { id: string; worker_id: string; amount: number; status?: string };
    if (entry.status === 'paid') return; // already paid
    const amount = Number(entry.amount) || 0;
    const { error: updateErr } = await supabase
      .from('worker_ledger_entries')
      .update({
        status: 'paid',
        paid_at: new Date().toISOString(),
        payment_reference: null, // Job rows must NOT carry PAY ref (canonical: only accounting_payment rows may)
      })
      .eq('id', entry.id);
    if (updateErr) throw new Error(`Ledger update failed: ${updateErr.message}`);
    // P1-3: balance is derived from GL — do not write workers.current_balance
  },

  /**
   * After an accounting_payment row is posted: mark unpaid studio_production_stage job rows paid (FIFO by created_at)
   * until the payment amount is exhausted. Does NOT change workers.current_balance (payment path already decrements once).
   * Fixes subledger vs payments mismatch (e.g. Integrity Lab "remaining due" vs GL).
   */
  async allocateUnpaidStageJobsAfterWorkerPayment(
    companyId: string,
    workerId: string,
    paymentAmount: number
  ): Promise<void> {
    if (!companyId || !workerId || paymentAmount <= 0.01) return;
    const { data: rows, error } = await supabase
      .from('worker_ledger_entries')
      .select('id, reference_id, amount, status, created_at')
      .eq('company_id', companyId)
      .eq('worker_id', workerId)
      .eq('reference_type', 'studio_production_stage')
      .order('created_at', { ascending: true, nullsFirst: true });
    if (error || !rows?.length) return;
    let pool = paymentAmount;
    for (const row of rows as { id: string; amount?: number; status?: string }[]) {
      if (String(row.status || '').toLowerCase() === 'paid') continue;
      const amt = Number(row.amount) || 0;
      if (amt <= 0.01) continue;
      if (pool + 1e-4 < amt) break;
      const { error: upErr } = await supabase
        .from('worker_ledger_entries')
        .update({
          status: 'paid',
          paid_at: new Date().toISOString(),
          payment_reference: null,
        })
        .eq('id', row.id);
      if (upErr) {
        console.warn('[studioProductionService] allocateUnpaidStageJobsAfterWorkerPayment:', upErr.message);
        continue;
      }
      pool -= amt;
      if (pool <= 1e-4) break;
    }
  },

  /**
   * Record an accounting payment in worker ledger (Accounting → Pay Worker flow).
   * Inserts one row: amount, status=paid, reference_type=accounting_payment.
   * Used when user pays worker from Ledger/Accounting without a specific stage (generic payment).
   */
  async recordAccountingPaymentToLedger(params: {
    companyId: string;
    workerId: string;
    amount: number;
    paymentReference?: string | null;
    notes?: string | null;
    /** Optional: use journal entry id for reference_id when available (links journal ↔ worker ledger) */
    journalEntryId?: string | null;
  }): Promise<void> {
    const { companyId, workerId, amount, paymentReference, notes, journalEntryId } = params;
    if (typeof window !== 'undefined') {
      console.log('[WORKER LEDGER DEBUG] recordAccountingPaymentToLedger called', { companyId, workerId, amount, journalEntryId });
    }
    if (!companyId || !workerId || amount <= 0) throw new Error('companyId, workerId, amount required');
    const refId = journalEntryId || crypto.randomUUID();

    // ONE REAL PAYMENT = ONE WORKER LEDGER ROW. Skip insert if a row already exists for this journal (idempotent).
    if (journalEntryId) {
      const { data: existing } = await supabase
        .from('worker_ledger_entries')
        .select('id')
        .eq('company_id', companyId)
        .eq('worker_id', workerId)
        .eq('reference_type', 'accounting_payment')
        .eq('reference_id', journalEntryId)
        .limit(1)
        .maybeSingle();
      if (existing) {
        if (typeof window !== 'undefined') {
          console.log('[WORKER LEDGER DEBUG] worker_ledger_entries skip duplicate (row exists)', { journalEntryId });
        }
        return;
      }
    }

    const { data: insertData, error } = await supabase.from('worker_ledger_entries').insert({
      company_id: companyId,
      worker_id: workerId,
      amount,
      reference_type: 'accounting_payment',
      reference_id: refId,
      status: 'paid',
      paid_at: new Date().toISOString(),
      payment_reference: paymentReference || null,
      notes: notes || `Payment via Accounting`,
    }).select('id').maybeSingle();
    if (typeof window !== 'undefined') {
      console.log('[WORKER LEDGER DEBUG] worker_ledger_entries insert result', { id: insertData?.id ?? null, error: error?.message ?? null });
    }
    if (error) throw new Error(`Worker ledger (accounting payment) failed: ${error.message}`);
    await this.allocateUnpaidStageJobsAfterWorkerPayment(companyId, workerId, amount);
    // P1-3: balance is derived from GL — do not write workers.current_balance
  },

  /**
   * One-off repair: replay `payments` (worker_payment, oldest first) and FIFO-allocate to unpaid stage job rows.
   * Does not change `workers.current_balance`. Safe to re-run; skips rows already `paid`.
   */
  async repairStageJobPaidFlagsFromWorkerPayments(companyId: string): Promise<{ paymentsProcessed: number }> {
    let paymentsProcessed = 0;
    const { data: pays, error } = await supabase
      .from('payments')
      .select('reference_id, amount, payment_date, created_at')
      .eq('company_id', companyId)
      .eq('reference_type', 'worker_payment')
      .order('payment_date', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) throw new Error(error.message);
    for (const p of pays || []) {
      const wid = (p as { reference_id?: string }).reference_id;
      const amt = Number((p as { amount?: number }).amount) || 0;
      if (!wid || amt <= 0.01) continue;
      await this.allocateUnpaidStageJobsAfterWorkerPayment(companyId, wid, amt);
      paymentsProcessed += 1;
    }
    return { paymentsProcessed };
  },

  /**
   * Record a salary payment in worker ledger (Expenses → Salary flow).
   * Inserts one row: amount, status=paid, reference_type=salary, reference_id=expenseId.
   * Workers list Due Balance = sum(unpaid); this entry is paid so it only increases total earnings.
   */
  async recordSalaryPayment(params: {
    companyId: string;
    workerId: string;
    amount: number;
    expenseId: string;
    paymentReference?: string | null;
    notes?: string | null;
  }): Promise<void> {
    const { companyId, workerId, amount, expenseId, paymentReference, notes } = params;
    if (!companyId || !workerId || amount <= 0 || !expenseId) throw new Error('companyId, workerId, amount, expenseId required');
    const { error } = await supabase.from('worker_ledger_entries').insert({
      company_id: companyId,
      worker_id: workerId,
      amount,
      reference_type: 'salary',
      reference_id: expenseId,
      status: 'paid',
      paid_at: new Date().toISOString(),
      payment_reference: paymentReference || null,
      notes: notes || `Salary payment`,
    });
    if (error) throw new Error(`Worker ledger (salary) failed: ${error.message}`);
  },

  /**
   * Studio summary for a sale (Sales Detail page: breakdown, status, duration).
   * Real-time: reads from studio_productions + studio_production_stages linked to sale_id.
   */
  async getStudioSummaryBySaleId(saleId: string): Promise<{
    hasStudio: boolean;
    productionStatus: 'in_progress' | 'completed' | 'none';
    totalStudioCost: number;
    tasksCompleted: number;
    tasksTotal: number;
    productionDurationDays: number | null;
    completedAt: string | null;
    breakdown: { stageType: string; amount: number; label: string }[];
    productions: { id: string; productionNo: string; status: string; completedAt: string | null }[];
    /** sales_items.id of the generated studio line. Use to get Final Sale Price for profit = Final - Cost. */
    generatedInvoiceItemId: string | null;
  }> {
    const empty = {
      hasStudio: false,
      productionStatus: 'none' as const,
      totalStudioCost: 0,
      tasksCompleted: 0,
      tasksTotal: 0,
      productionDurationDays: null,
      completedAt: null,
      breakdown: [],
      productions: [],
      generatedInvoiceItemId: null,
    };
    try {
      const productions = await this.getProductionsBySaleId(saleId);
      if (!productions.length) return empty;

      let totalCost = 0;
      let tasksCompleted = 0;
      let tasksTotal = 0;
      const byType: Record<string, number> = {};
      let latestCompletedAt: string | null = null;
      let earliestStart: string | null = null;

      for (const p of productions) {
        const stages = await this.getStagesByProductionId(p.id);
        tasksTotal += stages.length;
        for (const s of stages) {
          const cost = Number((s as any).cost) || 0;
          totalCost += cost;
          if ((s as any).status === 'completed') tasksCompleted++;
          const t = ((s as any).stage_type || '').toLowerCase();
          byType[t] = (byType[t] || 0) + cost;
          const completedAt = (s as any).completed_at;
          if (completedAt) {
            if (!latestCompletedAt || completedAt > latestCompletedAt) latestCompletedAt = completedAt;
          }
        }
        if (p.start_date) {
          if (!earliestStart || p.start_date < earliestStart) earliestStart = p.start_date;
        }
        if ((p as any).completed_at && (!latestCompletedAt || (p as any).completed_at > latestCompletedAt)) {
          latestCompletedAt = (p as any).completed_at;
        }
      }

      const stageLabels: Record<string, string> = {
        dyer: 'Dyeing',
        dyeing: 'Dyeing',
        stitching: 'Stitching',
        handwork: 'Handwork',
        embroidery: 'Embroidery',
      };
      const breakdown = Object.entries(byType)
        .filter(([, amt]) => amt > 0)
        .map(([stageType, amount]) => ({
          stageType,
          amount,
          label: stageLabels[stageType] || stageType.charAt(0).toUpperCase() + stageType.slice(1),
        }))
        .sort((a, b) => b.amount - a.amount);

      const allCompleted = productions.every((p) => p.status === 'completed');
      const anyInProgress = productions.some((p) => p.status === 'in_progress');
      let productionDurationDays: number | null = null;
      if (earliestStart && latestCompletedAt) {
        const start = new Date(earliestStart).getTime();
        const end = new Date(latestCompletedAt).getTime();
        productionDurationDays = Math.max(0, Math.ceil((end - start) / (24 * 60 * 60 * 1000)));
      }

      const firstWithItem = productions.find((p) => (p as any).generated_invoice_item_id);
      return {
        hasStudio: true,
        productionStatus: allCompleted ? 'completed' : anyInProgress ? 'in_progress' : 'none',
        totalStudioCost: totalCost,
        tasksCompleted,
        tasksTotal,
        productionDurationDays,
        completedAt: latestCompletedAt,
        breakdown,
        productions: productions.map((p) => ({
          id: p.id,
          productionNo: p.production_no,
          status: p.status,
          completedAt: (p as any).completed_at || null,
        })),
        generatedInvoiceItemId: firstWithItem ? (firstWithItem as any).generated_invoice_item_id : null,
      };
    } catch (e: any) {
      if (e?.code === 'PGRST116' || e?.message?.includes('does not exist')) return empty;
      throw e;
    }
  },

  /**
   * Sale cancelled: reverse **unpaid** studio stage bill JEs (Dr 5000 / Cr 2010) with an opposing reversal.
   * Deletes unpaid worker_ledger rows for those stages and clears stage cost / journal link.
   * If any stage cost was **paid**, sets `cancel_gl_review_needed` on the production (migration column).
   */
  async reverseUnpaidStudioStagesAfterSaleCancel(saleId: string, performedBy?: string | null): Promise<void> {
    if (!saleId) return;
    const { data: prod, error: pErr } = await supabase
      .from('studio_productions')
      .select('id, company_id, branch_id, production_no, status')
      .eq('sale_id', saleId)
      .maybeSingle();
    if (pErr || !prod) return;

    const production = prod as {
      id: string;
      company_id: string;
      branch_id: string | null;
      production_no: string;
      status?: string;
    };

    const { data: stageRows } = await supabase
      .from('studio_production_stages')
      .select('id, journal_entry_id, cost, assigned_worker_id, stage_type, status')
      .eq('production_id', production.id);

    let needsPaidReview = false;

    for (const st of stageRows || []) {
      const row = st as {
        id: string;
        journal_entry_id: string | null;
        cost: number;
        assigned_worker_id: string | null;
        stage_type: string;
      };
      const cost = Number(row.cost) || 0;
      if (!row.journal_entry_id || cost <= 0) continue;

      const { data: existingRev } = await supabase
        .from('journal_entries')
        .select('id')
        .eq('reference_type', 'studio_production_stage_reversal')
        .eq('reference_id', row.id)
        .ilike('description', '%Sale cancelled – reverse%')
        .maybeSingle();
      if (existingRev?.id) continue;

      const { data: led } = await supabase
        .from('worker_ledger_entries')
        .select('id, status')
        .eq('reference_type', 'studio_production_stage')
        .eq('reference_id', row.id)
        .maybeSingle();
      const paid = String((led as { status?: string } | null)?.status || '').toLowerCase() === 'paid';

      if (paid) {
        needsPaidReview = true;
        continue;
      }

      await createProductionCostReversalEntry({
        companyId: production.company_id,
        branchId: production.branch_id,
        stageId: row.id,
        workerId: row.assigned_worker_id,
        productionNo: production.production_no,
        amount: cost,
        stageType: row.stage_type || 'stage',
        performedBy,
        description: `Sale cancelled – reverse stage accrual (${production.production_no}) – ${row.stage_type || 'stage'}`,
      });

      if ((led as { id?: string } | null)?.id) {
        await supabase.from('worker_ledger_entries').delete().eq('id', (led as { id: string }).id);
      }

      await supabase
        .from('studio_production_stages')
        .update({ journal_entry_id: null, cost: 0 })
        .eq('id', row.id);
    }

    const prodPatch: Record<string, unknown> = { status: 'cancelled' };
    if (needsPaidReview) {
      prodPatch.cancel_gl_review_needed = true;
    }

    const { error: upProdErr } = await supabase.from('studio_productions').update(prodPatch).eq('id', production.id);
    if (upProdErr && /cancel_gl_review_needed|does not exist/i.test(upProdErr.message || '')) {
      await supabase.from('studio_productions').update({ status: 'cancelled' }).eq('id', production.id);
    }
  },
};
